import { useEffect, useRef, useState } from "react";
import { Group, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";
import type { ConsumptionRow, ProductType, StockItem } from "../../api/types";
import { ResponsiveTable } from "../../components/responsiveTable/ResponsiveTable";
import { stockErrorMessage } from "./errors";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// "YYYY-MM" → prima zi a lunii; `to` e exclusiv, deci prima zi a lunii următoare.
function monthStart(m: string, plus = 0): string {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + plus, 1));
  return d.toISOString().slice(0, 10);
}

export default function ConsumptionTab() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(currentMonth());
  const [to, setTo] = useState(currentMonth());
  const [typeId, setTypeId] = useState<string | null>(null);
  const [itemId, setItemId] = useState<string | null>(null);
  const [types, setTypes] = useState<ProductType[]>([]);
  const [items, setItems] = useState<StockItem[]>([]);
  const [rows, setRows] = useState<ConsumptionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const reqSeq = useRef(0);

  useEffect(() => {
    void (async () => {
      try {
        const [tr, ir] = await Promise.all([api.listProductTypes(), api.listStock()]);
        setTypes(tr.types);
        setItems(ir.items);
      } catch (e) {
        notifications.show({ color: "red", message: stockErrorMessage(e, t) });
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!from || !to) return;
    const seq = ++reqSeq.current; // răspunsurile cererilor depășite se ignoră
    setLoading(true);
    api.stockConsumption({
      from: monthStart(from), to: monthStart(to, 1),
      product_type_id: typeId ? Number(typeId) : null,
      stock_item_id: itemId ? Number(itemId) : null,
    })
      .then((r) => { if (seq === reqSeq.current) setRows(r.rows); })
      .catch((e) => { if (seq === reqSeq.current) notifications.show({ color: "red", message: stockErrorMessage(e, t) }); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
    // eslint-disable-next-line
  }, [from, to, typeId, itemId]);

  return (
    <Stack>
      <Group align="flex-end" gap="sm">
        <TextInput type="month" label={`${t("stock.period")} — ${t("stock.periodFrom")}`} value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        <TextInput type="month" label={t("stock.periodTo")} value={to} onChange={(e) => setTo(e.currentTarget.value)} />
        <Select
          clearable placeholder={t("stock.all")} label={t("stock.productType")}
          data={types.map((x) => ({ value: String(x.id), label: x.name }))}
          value={typeId} onChange={setTypeId}
        />
        <Select
          clearable searchable placeholder={t("stock.all")} label={t("stock.colName")}
          data={items.map((i) => ({ value: String(i.id), label: i.name }))}
          value={itemId} onChange={setItemId}
        />
      </Group>
      <ResponsiveTable
        mobile={{
          rows,
          rowKey: (r) => `${r.stock_item_id}-${r.product_type_id}`,
          title: (r) => r.stock_item_name,
          subtitle: (r) => `${r.consumed} ${r.unit}`,
          fields: [{ label: t("stock.productType"), render: (r) => r.product_type_name ?? "—" }],
          loading: loading && rows.length === 0,
          empty: t("stock.noConsumption"),
        }}
      >
      <Table.ScrollContainer minWidth={500}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("stock.colName")}</Table.Th>
              <Table.Th>{t("stock.productType")}</Table.Th>
              <Table.Th>{t("stock.consumed")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!loading && rows.length === 0 && (
              <Table.Tr><Table.Td colSpan={3}><Text c="dimmed">{t("stock.noConsumption")}</Text></Table.Td></Table.Tr>
            )}
            {rows.map((r) => (
              <Table.Tr key={`${r.stock_item_id}-${r.product_type_id}`}>
                <Table.Td>{r.stock_item_name}</Table.Td>
                <Table.Td>{r.product_type_name ?? "—"}</Table.Td>
                <Table.Td>{r.consumed} {r.unit}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>
    </Stack>
  );
}
